import { FileBarChart2, FileChartLine } from 'lucide-react';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { DownloadIndividualReportPdf } from './DownloadIndividualReportPdf';
import { IndividualModalValues } from './IndividualStudentHeader';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { ProgressBar } from 'primereact/progressbar';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  selectedIndividualStudentReportState,
  setIndividualStudentReport,
} from '../../redux/reducers/individualStudentReportSlice';
import { MaskedInput } from '../MaskedData/MaskedInput';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { TimesCircleIcon } from 'primereact/icons/timescircle';

interface Props {
  modalValues: IndividualModalValues;
  setModalValues: Dispatch<SetStateAction<IndividualModalValues>>;
}

export enum IndividualSingleOrBulk {
  SINGLE,
  BULK,
}

export enum IndividualReportType {
  SUMMARY,
  DETAIL,
}

export interface ModalSelectedValues {
  reportType: IndividualReportType | null;
  singleOrBulkType: IndividualSingleOrBulk | null;
  selectedStudents: string[] | null;
  signature: boolean;
}

export interface DropdownItemModel {
  publicUserId: string;
  value: string;
  label: string;
}

export const IndividualStudentReportPdfModal: FC<Props> = ({ modalValues, setModalValues }) => {
  const isrRedux = useAppSelector(selectedIndividualStudentReportState);
  const [studentsOptionList, setStudentsOptionList] = useState<DropdownItemModel[]>([]);
  const [modalSelectedValues, setModalSelectedValues] = useState<ModalSelectedValues>({
    reportType: null,
    singleOrBulkType: null,
    selectedStudents: null,
    signature: true,
  });
  const [pdfSkip, setPdfSkip] = useState(true);
  const dispatch = useAppDispatch();

  const resetModal = () => {
    setPdfSkip(true);
    setModalSelectedValues({ ...modalSelectedValues, reportType: null, signature: true });
    setModalValues({ userList: [], usersId: null, visible: false });
  };

  useEffect(() => {
    setStudentsOptionList(
      modalValues.userList.map(el => {
        return {
          label: el.firstName + ' ' + el.lastName,
          value: String(el.id),
          publicUserId: el.publicUserId,
        };
      }),
    );
    setModalSelectedValues({ ...modalSelectedValues, selectedStudents: modalValues.userList.map(el => el.id) });
  }, [modalValues.userList]);

  return (
    <Dialog
      closable
      className="nc_individual-report-skill-modal"
      style={{ maxHeight: '90vh' }}
      header={
        <div>
          <h3>Download student report</h3>
        </div>
      }
      footer={
        <div className="tw-w-full">
          {isrRedux.isLoadingPdf && (
            <div className="tw-bg-neutral-100 tw-w-full tw-my-3 tw-py-4 tw-rounded-lg tw-flex tw-justify-center tw-flex-col">
              <div className="tw-w-[90%] tw-mx-auto">
                <p className="tw-text-neutral-900 tw-font-semibold tw-w-full tw-pb-0 tw-mb-0 tw-w-full tw-text-start">
                  Please keep this window open until the download is complete.
                </p>
                <p className="tw-text-neutral-900 tw-font-semibold tw-w-fulñ tw-pt-1 tw-mt-1 tw-w-full tw-text-start">
                  The download may include multiple zip folders.
                </p>
              </div>
              <ProgressBar
                mode="indeterminate"
                style={{
                  height: '9px',
                  backgroundColor: '#2365f380',
                  width: '90%',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
                showValue={false}
                className="inline-block"
              ></ProgressBar>
            </div>
          )}
          <div className="tw-flex tw-justify-end tw-w-full">
            <Button
              label="Cancel"
              outlined
              className="tw-float-right"
              onClick={() => {
                dispatch(setIndividualStudentReport({ ...isrRedux, isLoadingPdf: false }));
                resetModal();
              }}
            />
            <Suspense fallback={<Button disabled label="Download" />}>
              <DownloadIndividualReportPdf
                setPdfSkip={setPdfSkip}
                modalSelectedValues={modalSelectedValues}
                resetModal={resetModal}
                userIdsToPDF={modalValues.usersId}
                pdfSkip={pdfSkip}
                isSummary={modalSelectedValues.reportType === IndividualReportType.SUMMARY}
              />
            </Suspense>
          </div>
        </div>
      }
      visible={modalValues.visible}
      onHide={() => {
        dispatch(setIndividualStudentReport({ ...isrRedux, isLoadingPdf: false }));
        resetModal();
      }}
    >
      <div className="min-h-fit">
        <div className="tw-flex tw-flex-col tw-gap-y-4 tw-pb-6 tw-border-b-[1px] tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-b-[#F1F0EE]">
          <div className="tw-flex tw-flex-row tw-justify-between tw-gap-x-5 ">
            <div>
              <label htmlFor="grouped1" className="flex flex-row">
                <input
                  style={{ height: '20px', width: '20px' }}
                  type="radio"
                  id="grouped1"
                  name="type"
                  value={IndividualSingleOrBulk.SINGLE}
                  onChange={() => {
                    setModalSelectedValues({ ...modalSelectedValues, singleOrBulkType: IndividualSingleOrBulk.SINGLE });
                  }}
                  checked={modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.SINGLE}
                />
                <p className="tw-ml-2 tw-my-0 tw-cursor-pointer">Single download</p>
              </label>
              <p className="tw-m-0 tw-text-[#7C7C7C] tw-text-sm tw-pl-[2.2rem]">
                Download report for the current student.
              </p>
            </div>
            <div>
              <label htmlFor="grouped2" className="flex flex-row">
                <input
                  style={{ height: '20px', width: '20px' }}
                  type="radio"
                  id="grouped2"
                  name="type"
                  value={IndividualSingleOrBulk.BULK}
                  onChange={() => {
                    setModalSelectedValues({ ...modalSelectedValues, singleOrBulkType: IndividualSingleOrBulk.BULK });
                  }}
                  checked={modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.BULK}
                />
                <p className="tw-ml-2 tw-my-0 tw-cursor-pointer">Bulk download</p>
              </label>
              <p className="tw-m-0 tw-text-[#7C7C7C] tw-text-sm tw-pl-[2.2rem]">
                Download reports for multiple students at once.
              </p>
            </div>
          </div>
          {modalSelectedValues.singleOrBulkType === IndividualSingleOrBulk.BULK && (
            <div className="tw-w-full">
              <p className="tw-m-0 tw-text-lg">Select students:</p>
              <div className="p-inputgroup tw-h-[33px]">
                <MaskedInput>
                  <MultiSelect
                    onChange={(e: MultiSelectChangeEvent) => {
                      setModalSelectedValues({ ...modalSelectedValues, selectedStudents: e.value });
                    }}
                    value={modalSelectedValues.selectedStudents}
                    options={studentsOptionList}
                    optionLabel="label"
                    itemTemplate={(item: DropdownItemModel) => (
                      <MaskedStudentData student={item} type={PiiTypes.FullName}>
                        {item.label}
                      </MaskedStudentData>
                    )}
                    selectedItemTemplate={(value?: string) => {
                      const item = studentsOptionList.find(item => item.value === value);
                      if (item) {
                        return (
                          <div className="p-multiselect-token">
                            <MaskedStudentData
                              className="p-multiselect-token-label"
                              student={item}
                              type={PiiTypes.FullName}
                            >
                              {item?.label}
                            </MaskedStudentData>
                            <TimesCircleIcon
                              role="button"
                              tabIndex={0}
                              className="p-multiselect-token-icon"
                              onClick={e => {
                                e.stopPropagation();
                                setModalSelectedValues({
                                  ...modalSelectedValues,
                                  selectedStudents:
                                    modalSelectedValues.selectedStudents?.filter(v => v !== value) ?? [],
                                });
                              }}
                              key={`${value}-removeIcon`}
                            />
                          </div>
                        );
                      } else {
                        return ' '; // It needs to be a single space in that string. Empty string doesn't work.
                      }
                    }}
                    id="lessons"
                    filter
                    display="chip"
                    className={`nc_individual-report-modal-autocomplete ${
                      modalSelectedValues.selectedStudents?.length === studentsOptionList.length ? 'all-selected' : ''
                    }`}
                    data-all-label={`All students in class (${studentsOptionList.length})`}
                  />
                </MaskedInput>
              </div>
            </div>
          )}
        </div>

        <div className="tw-flex tw-p-5 tw-justify-center tw-gap-x-5">
          <div
            onClick={() => {
              setModalSelectedValues({ ...modalSelectedValues, reportType: IndividualReportType.SUMMARY });
            }}
            className={`${
              modalSelectedValues.reportType === IndividualReportType.SUMMARY
                ? 'nc_individual-report-border-active'
                : ''
            } tw-w-[48%] tw-cursor-pointer tw-border-neutral-300 hover:tw-border-[#9FABFD] tw-border-[2px] tw-py-4 tw-px-3 tw-border-solid tw-rounded-xl`}
          >
            <div className="tw-flex tw-justify-center tw-my-3">
              <FileChartLine size={30} className="tw-text-blue-500" />
            </div>

            <h4 className="tw-text-center">Summary report</h4>
            <p className="tw-text-center">A condensed version highlighting student progress.</p>
            <p className="tw-font-semibold tw-text-center tw-cursor-pointer">Ideal for parent-teacher conferences.</p>
          </div>

          <div
            //temporarily disabled, className are been trimmed
            onClick={() => {
              setModalSelectedValues({ ...modalSelectedValues, reportType: IndividualReportType.DETAIL });
            }}
            className={`${
              modalSelectedValues.reportType === IndividualReportType.DETAIL ? 'nc_individual-report-border-active' : ''
            } tw-w-[48%] tw-border-neutral-300 tw-border-[2px] tw-py-4 tw-px-3 tw-border-solid tw-rounded-xl`}
          >
            {/* add a comming soon! indicator */}

            <div className="tw-flex tw-justify-center tw-my-3">
              <FileBarChart2
                size={30}
                //temporarily disabled
                // className="tw-text-blue-500"
              />
            </div>

            <h4 className="tw-text-center">Detailed report</h4>
            <p className="tw-text-center">A comprehensive view with complete assessment data. </p>
            <p className="tw-font-semibold tw-text-center">Best for in-depth analysis and record-keeping.</p>
          </div>
        </div>

        <div className="tw-p-5">
          <Checkbox
            checked={modalSelectedValues.signature}
            onChange={() => {
              setModalSelectedValues({ ...modalSelectedValues, signature: !modalSelectedValues.signature });
            }}
          />
          <span>Add Parent/Guardian acknowledgment (requires signature on report).</span>
        </div>
      </div>
    </Dialog>
  );
};
